class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'true');
    cartLink.setAttribute('aria-expanded', 'false');

    // Toggle, not open-only: the pill is the panel's trigger the same way the search icon is
    // the search panel's, so pressing it again has to put it away.
    // `active` is added inside open()'s setTimeout, so read the state BEFORE acting on it.
    const toggle = (event) => {
      event.preventDefault();
      if (this.classList.contains('active')) this.close();
      else this.open(cartLink);
    };

    cartLink.addEventListener('click', toggle);
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') toggle(event);
    });
  }

  // AS PISCINE (2026-07-26): this is a header DROPDOWN, not a modal — see snippets/cart-drawer.liquid.
  // So three stock behaviours are deliberately gone: the body scroll lock, the focus trap, and the
  // page-dimming overlay. A dropdown that traps focus and freezes the page behind it is a modal in
  // everything but appearance, and it fought the nav/search panels it now sits alongside. Closing is
  // handled the same way those two do it (Esc / outside click / re-click the trigger) in asp-motion.js.
  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active', 'is-opening');
    });

    // `is-opening` gates the staggered entrance so it plays on open only, never on the
    // re-render that follows a quantity change. 900ms > the 500ms run + 225ms max delay.
    clearTimeout(this.openingTimer);
    this.openingTimer = setTimeout(() => this.classList.remove('is-opening'), 900);

    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
    // re-bind the cursor-glow / hover-ring motion to whatever the last re-render put in here
    window.ASP?.bindMotion?.(this);

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active', 'is-opening');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    // We never call trapFocus, but cart.js still does after a quantity change. Releasing it here
    // (with no argument, so focus isn't yanked back to the pill) stops a closed panel from
    // holding the Tab key hostage.
    removeTrapFocus();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
